import { ReactElement, ReactNode, useMemo } from "react";

import { Outlet } from "react-router-dom";

import { Entity } from "@/jenesys-types/data/common";

import { BreadcrumbItem } from "@/components/common/breadcrumbs/BreadcrumbItem";
import { CrudRetrieveContextProvider } from "@/components/common/crud/CrudRetrieveContext/CrudRetireveContext";
import { LoadingErrorContentSwitch } from "@/components/common/errors/LoadingErrorContentSwitch";
import useCrudRetrieve from "@/hooks/crud/useCrudRetrieve";
import { getTitle } from "@/util/crud";
import { Accessor } from "@/util/data";

export interface CrudRetrieveProps<E extends Entity> {
  element?: ReactNode;
  relationships?: Array<string>;
  titleAccessor?: Accessor<E, string>;
}

export function CrudRetrieve<E extends Entity = Entity>({
  element = <Outlet />,
  relationships,
  titleAccessor
}: CrudRetrieveProps<E>): ReactElement<any, any> | null {
  const { dataType, error, isLoading, itemData } =
    useCrudRetrieve<E>(relationships);

  const title = useMemo(
    () => (itemData ? getTitle(itemData as E, titleAccessor) : ""),
    [itemData, titleAccessor]
  );

  return (
    <LoadingErrorContentSwitch
      isLoading={isLoading}
      error={error}
      errorTitle={`Error Loading Data ${dataType}`}
    >
      {!!itemData && (
        <CrudRetrieveContextProvider<E> dataType={dataType} data={itemData}>
          {title && <BreadcrumbItem title={title} />}
          {element}
        </CrudRetrieveContextProvider>
      )}
    </LoadingErrorContentSwitch>
  );
}
